import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, gbp, fmtDate, downloadPdf, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, RefreshCw, BookOpen, FileSpreadsheet, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import POPayments from "@/components/POPayments";

// Status is automatic: fully paid = All clear, some paid = Part paid, nothing yet = Open.
export const poPayStatus = (r) => {
  const amt = Number(r.amount_paid) || 0;
  if (r.paid || (amt > 0 && amt >= (Number(r.total) || 0) - 0.005)) return "clear";
  return amt > 0 ? "part" : "open";
};

const STATUS_STYLES = {
  clear: ["All clear", "border-emerald-300 bg-emerald-50 text-emerald-800"],
  part: ["Part paid", "border-amber-300 bg-amber-50 text-amber-800"],
  open: ["Open", "border-slate-300 bg-slate-50 text-slate-700"],
};

function StatusBadge({ r }) {
  const [label, cls] = STATUS_STYLES[poPayStatus(r)];
  return <span data-testid={`po-reg-status-${r.id}`} className={`inline-flex whitespace-nowrap text-xs font-semibold border rounded-sm px-2 py-1 ${cls}`}>{label}</span>;
}

export default function PurchaseOrderRegister() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showPaid, setShowPaid] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const onPaymentsChange = (d) => {
    setRows((prev) => prev.map((r) => (r.id === d.id ? { ...r, amount_paid: d.amount_paid, paid: d.paid, status: d.status ?? r.status, payments: d.payments } : r)));
    setPayFor((p) => (p && p.id === d.id ? { ...p, payments: d.payments, amount_paid: d.amount_paid } : p));
  };
  const [saving, setSaving] = useState({}); // id -> bool

  const load = () => {
    setLoading(true);
    api.get("/purchase-orders-register").then((r) => setRows(r.data.rows)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = rows.filter((r) => {
    const s = q.toLowerCase();
    if (!s) return true;
    return (
      r.po_number.toLowerCase().includes(s) ||
      (r.reference || "").toLowerCase().includes(s) ||
      (r.supplier || "").toLowerCase().includes(s) ||
      (r.description || "").toLowerCase().includes(s) ||
      (r.invoice_number || "").toLowerCase().includes(s)
    );
  });

  const sum = (list, k) => list.reduce((s, r) => s + (Number(r[k]) || 0), 0);
  const unpaid = filtered.filter((r) => !r.paid);
  const paid = filtered.filter((r) => r.paid);
  const subtotalSum = sum(unpaid, "subtotal");
  const vatSum = sum(unpaid, "tax_amount");
  const totalSum = sum(unpaid, "total");
  const paidSum = sum(paid, "total");
  const outstanding = unpaid.reduce((s, r) => s + (Number(r.total) || 0) - (Number(r.amount_paid) || 0), 0);

  const patchRow = async (id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    setSaving((s) => ({ ...s, [id]: true }));
    try {
      const res = await api.patch(`/purchase-orders/${id}/tracking`, patch);
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, paid: !!res.data.paid, status: res.data.status || "" } : r)));
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    } finally {
      setSaving((s) => ({ ...s, [id]: false }));
    }
  };

  const onExport = async () => {
    try {
      await downloadPdf("/purchase-orders-register/export.xlsx", "D4PS-Purchase-Order-Register.xlsx");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const renderRow = (r) => (
    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`po-register-row-${r.id}`}>
      <td className="px-2 py-2 font-medium whitespace-nowrap">
        <Link to={`/purchase-orders/${r.id}`} className="hover:underline">{r.po_number}</Link>
      </td>
      <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{fmtDate(r.date)}</td>
      <td className="px-2 py-2 text-slate-700">{r.reference || "—"}</td>
      <td className="px-2 py-2 text-slate-700">{r.supplier || "—"}</td>
      <td className="px-2 py-2 text-slate-700 max-w-[9rem] truncate" title={r.description}>{r.description || "—"}</td>
      <td className="px-2 py-2 text-right tabular-nums">{gbp(r.subtotal)}</td>
      <td className="px-2 py-2 text-right tabular-nums text-slate-500">{gbp(r.tax_amount)}</td>
      <td className="px-2 py-2 text-right tabular-nums font-medium">{gbp(r.total)}</td>
      <td className="px-2 py-2 text-center">
        <Checkbox data-testid={`po-reg-invrec-${r.id}`} checked={!!r.invoice_received} onCheckedChange={(v) => patchRow(r.id, { invoice_received: !!v })} />
      </td>
      <td className="px-2 py-2">
        <Input data-testid={`po-reg-invno-${r.id}`} value={r.invoice_number || ""}
          onChange={(e) => setRows((prev) => prev.map((x) => x.id === r.id ? { ...x, invoice_number: e.target.value } : x))}
          onBlur={(e) => patchRow(r.id, { invoice_number: e.target.value })}
          className="h-8 w-20 rounded-sm border-slate-300 px-1.5 text-xs" placeholder="—" />
      </td>
      <td className="px-2 py-2 text-center">
        <Checkbox data-testid={`po-reg-paid-${r.id}`} checked={!!r.paid} onCheckedChange={(v) => patchRow(r.id, { paid: !!v })} />
      </td>
      <td className="px-2 py-2">
        <button type="button" onClick={() => setPayFor(r)} data-testid={`po-reg-amtpaid-${r.id}`}
          className="text-left rounded-sm border border-slate-300 hover:border-slate-500 hover:bg-white px-2 py-1 w-24">
          <div className="tabular-nums text-xs font-medium">{gbp(Number(r.amount_paid) || 0)}</div>
          <div className="text-[10px] text-slate-500">{(r.payments || []).length} payment{(r.payments || []).length === 1 ? "" : "s"} · log</div>
        </button>
        {Number(r.amount_paid) > 0 && (
          <div data-testid={`po-reg-balance-${r.id}`} className={`text-[11px] mt-0.5 tabular-nums ${Number(r.amount_paid) >= r.total - 0.005 ? "text-emerald-700" : "text-amber-700"}`}>
            {Number(r.amount_paid) >= r.total - 0.005 ? "Fully paid" : `${gbp(r.total - Number(r.amount_paid))} left`}
          </div>
        )}
      </td>
      <td className="px-2 py-2">
        <StatusBadge r={r} />
      </td>
    </tr>
  );

  return (
    <div data-testid="po-register-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Reporting</div>
          <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-1 flex items-center gap-3">
            <BookOpen className="h-7 w-7 text-slate-700" />
            Purchase Order Register
          </h1>
          <p className="text-slate-600 mt-2 text-sm">
            Auto-updated PO log. Track invoice receipt, payment, and status inline.
          </p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="po-register-refresh" variant="outline" className="rounded-sm border-slate-300" onClick={load}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Button data-testid="po-register-export-btn" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" onClick={onExport}>
            <FileSpreadsheet className="h-4 w-4 mr-2" /> Export Excel
          </Button>
        </div>
      </div>

      <Card className="rounded-sm border border-slate-200 shadow-none">
        <div className="p-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
          <div className="relative w-full max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="po-register-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search PO #, reference, supplier, description or invoice #…"
              className="pl-9 rounded-sm border-slate-300"
            />
          </div>
          <div className="ml-auto text-xs text-slate-500" data-testid="po-register-summary">
            {unpaid.length} unpaid · {gbp(outstanding)} outstanding · {paid.length} paid
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full po-table text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-normal">
                <th className="px-2 py-3 font-semibold">PO #</th>
                <th className="px-2 py-3 font-semibold">Date</th>
                <th className="px-2 py-3 font-semibold">Reference</th>
                <th className="px-2 py-3 font-semibold">Supplier</th>
                <th className="px-2 py-3 font-semibold">Description</th>
                <th className="px-2 py-3 font-semibold text-right">Subtotal</th>
                <th className="px-2 py-3 font-semibold text-right">VAT</th>
                <th className="px-2 py-3 font-semibold text-right">Total</th>
                <th className="px-2 py-3 font-semibold text-center">Inv Rec?</th>
                <th className="px-2 py-3 font-semibold">Inv No</th>
                <th className="px-2 py-3 font-semibold text-center">Paid</th>
                <th className="px-2 py-3 font-semibold">Amount Paid</th>
                <th className="px-2 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="text-slate-900">
              {loading && (
                <tr><td colSpan="13" className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan="13" className="px-4 py-12 text-center">
                    <div className="text-slate-700 font-medium">No purchase orders yet</div>
                    <div className="text-slate-500 text-sm mt-1">
                      Create a PO and it will appear in the register automatically.
                    </div>
                  </td>
                </tr>
              )}
              {!loading && filtered.length > 0 && unpaid.length === 0 && (
                <tr><td colSpan="13" className="px-4 py-6 text-center text-sm text-slate-500" data-testid="po-register-all-paid">All purchase orders are paid.</td></tr>
              )}
              {!loading && unpaid.map(renderRow)}
              {!loading && paid.length > 0 && (
                <tr className="border-t border-slate-200 bg-slate-50 cursor-pointer select-none hover:bg-slate-100" onClick={() => setShowPaid((v) => !v)} data-testid="po-register-paid-toggle">
                  <td colSpan="7" className="px-2 py-3 text-xs uppercase tracking-wider font-semibold text-slate-600">
                    <span className="inline-flex items-center gap-2">
                      <ChevronRight className={`h-4 w-4 transition-transform ${showPaid ? "rotate-90" : ""}`} />
                      Paid purchase orders ({paid.length})
                    </span>
                  </td>
                  <td className="px-2 py-3 text-right tabular-nums font-medium text-emerald-700">{gbp(paidSum)}</td>
                  <td colSpan="5"></td>
                </tr>
              )}
              {!loading && showPaid && paid.map(renderRow)}
            </tbody>
            {!loading && unpaid.length > 0 && (
              <tfoot>
                <tr className="border-t border-slate-200 bg-slate-50">
                  <td colSpan="5" className="px-2 py-3 text-right text-xs uppercase tracking-wider font-semibold text-slate-600">
                    Unpaid totals
                  </td>
                  <td className="px-2 py-3 text-right tabular-nums font-display font-semibold text-slate-900">{gbp(subtotalSum)}</td>
                  <td className="px-2 py-3 text-right tabular-nums font-display font-semibold text-slate-900">{gbp(vatSum)}</td>
                  <td className="px-2 py-3 text-right tabular-nums font-display font-semibold text-slate-900">{gbp(totalSum)}</td>
                  <td colSpan="5"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>
      <Dialog open={!!payFor} onOpenChange={(o) => { if (!o) setPayFor(null); }}>
        <DialogContent className="max-w-2xl" data-testid="po-payments-dialog">
          <DialogHeader><DialogTitle>Payments · {payFor?.po_number} · {payFor?.supplier}</DialogTitle></DialogHeader>
          {payFor && <POPayments key={payFor.id} poId={payFor.id} total={payFor.total} payments={payFor.payments || []} onChange={onPaymentsChange} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
